'use client';
import * as React from 'react';
import type { Transition, Variants } from 'framer-motion';
import { motion, useAnimation } from 'framer-motion';
import { useTheme } from 'next-themes';

const moonVariants: Variants = {
	normal: {
		rotate: 0,
	},
	animate: {
		rotate: [0, -10, 10, -5, 5, 0],
	},
};

const pathVariants: Variants = {
	normal: { opacity: 1 },
	animate: (i: number) => ({
		opacity: [0, 1],
		transition: { delay: i * 0.1, duration: 0.3 },
	}),
};

const svgTransition: Transition = {
	duration: 1.2,
	ease: 'easeInOut',
};

interface IconProps {
	isHovered: boolean;
}

export function DarkModeIcon({ isHovered }: IconProps) {
	const controls = useAnimation();
	const { theme } = useTheme();
	const isDark = theme === 'dark';

	React.useEffect(() => {
		void controls.start(isHovered ? 'animate' : 'normal');
	}, [isHovered, controls]);

	if (isDark) {
		return (
			<div className='select-none p-2 flex items-center justify-center'>
				<motion.svg
					xmlns='http://www.w3.org/2000/svg'
					width='28'
					height='28'
					viewBox='0 0 24 24'
					fill='none'
					stroke={isHovered ? '#FF69B4' : 'currentColor'}
					strokeWidth='2'
					strokeLinecap='round'
					strokeLinejoin='round'
					variants={moonVariants}
					animate={controls}
					transition={svgTransition}
				>
					<path d='M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z' />
				</motion.svg>
			</div>
		);
	}

	return (
		<div className='select-none p-2 flex items-center justify-center'>
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='28'
				height='28'
				viewBox='0 0 24 24'
				fill='none'
				stroke={isHovered ? '#FF69B4' : 'currentColor'}
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
			>
				<circle cx='12' cy='12' r='4' />
				{[
					'M12 2v2',
					'm19.07 4.93-1.41 1.41',
					'M20 12h2',
					'm17.66 17.66 1.41 1.41',
					'M12 20v2',
					'm6.34 17.66-1.41 1.41',
					'M2 12h2',
					'm4.93 4.93 1.41 1.41',
				].map((d, index) => (
					<motion.path
						key={d}
						d={d}
						animate={controls}
						variants={pathVariants}
						custom={index + 1}
					/>
				))}
			</svg>
		</div>
	);
}
